import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { AUTH_CATEGORIES, AUTH_STATUS, useAuth } from '../components/Auth';
import ElevatedLandingPage from '../components/ElevatedLandingPage/ElevatedLandingPage';
import { useFeatureFlags } from '../components/FeatureFlag/FeatureFlagsProvider';
import Spinner from '../components/Spinner/Spinner';
import { CHECKOUT_ELIGIBILITY_STATUS } from '../utils/serviceCalls/account';
import { useProviderCheckoutEligibility } from './hooks/useProviderCheckoutEligibility';
import { PageTracker } from './PageTracker';
import { usePolicyRedirect } from './PolicyRedirectGuard';
import { ROUTES } from './routes';

// #region JSDoc Types

/**
 * @typedef {import('../components/Auth').UserCategory} UserCategory
 */

// #endregion

/**
 * Wrapper for public pages.
 *
 * Currently a pass-through, but provides a consistent API and
 * a hook point for future public-page-specific behavior.
 *
 * @param {{ children: React.ReactNode }} props
 */
export function PublicPage({ children }) {
  return children;
}

/**
 * Protected route wrapper that enforces authentication and role-based access.
 *
 * Shows a spinner while auth is loading, then either renders children
 * or redirects to the landing page if the user lacks access.
 *
 * @param {{
 *   children: React.ReactNode,
 *   allowedCategories?: UserCategory[],
 *   redirectTo?: string
 * }} props
 */
export function ProtectedPage({
  children,
  allowedCategories = [],
  redirectTo = ROUTES.LANDING_PAGE.path,
}) {
  const { status, userCategory } = useAuth();
  const location = useLocation();
  const { DisableAuth } = useFeatureFlags();

  if (status === AUTH_STATUS.LOADING) {
    return <Spinner />;
  }

  const isAllowedCategory =
    // Empty allowedCategories means "allow all authenticated users"
    allowedCategories.length === 0 || allowedCategories.includes(userCategory);

  const hasAccess = status === AUTH_STATUS.AUTHENTICATED && isAllowedCategory;

  if (hasAccess) {
    return children;
  }

  // Dev bypass for local testing/storybook
  if (process.env.NODE_ENV === 'development' && DisableAuth) {
    console.warn(
      `[Routing] Auth disabled: allowing access to ${location.pathname}`,
    );
    return children;
  }

  if (process.env.NODE_ENV === 'development') {
    const reason =
      status === AUTH_STATUS.UNAUTHENTICATED
        ? 'Not authenticated'
        : userCategory === AUTH_CATEGORIES.UNSUPPORTED
          ? 'UNSUPPORTED user category'
          : userCategory === AUTH_CATEGORIES.UNKNOWN
            ? 'UNKNOWN user category'
            : `Category "${userCategory}" not in allowed list`;

    console.groupCollapsed(
      `[Routing] Access denied: ${location.pathname} -> ${redirectTo}`,
    );
    console.log('Reason:', reason);
    console.log('Auth status:', status);
    console.log('User category:', userCategory);
    console.log(
      'Allowed categories:',
      allowedCategories.length ? allowedCategories : 'any authenticated',
    );
    console.groupEnd();
  }

  return <Navigate to={redirectTo} state={{ from: location }} replace />;
}

/**
 * Blocks ineligible Providers from checkout (landing or deep-link).
 * Redirects to HGPro/Next dashboard with `fromSource=self-serve-failure`.
 *
 * @param {{ children: React.ReactNode }} props
 */
export function ProviderCheckoutEligibilityGuard({ children }) {
  const { status, userCategory, user } = useAuth();
  const isProvider =
    status === AUTH_STATUS.AUTHENTICATED &&
    userCategory === AUTH_CATEGORIES.PROVIDER;

  const { status: eligibilityStatus } = useProviderCheckoutEligibility({
    enabled: isProvider,
    pwid: user?.pwid,
  });

  if (!isProvider) {
    return children;
  }

  if (eligibilityStatus !== CHECKOUT_ELIGIBILITY_STATUS.ALLOWED) {
    return <Spinner />;
  }

  return children;
}

/**
 * Special guard for the landing page with auth-based routing.
 *
 * Handles:
 * - ?policy=terms-of-service redirect (legacy URL support)
 * - Provider users -> eligibility check, then /checkout (or ineligible dashboard)
 * - Admin users -> redirect to /add-providers-to-roster
 * - Unauthenticated/unknown -> show landing page
 */
export function LandingPageGuard() {
  const { status, userCategory, user } = useAuth();

  const isProvider =
    status === AUTH_STATUS.AUTHENTICATED &&
    userCategory === AUTH_CATEGORIES.PROVIDER;

  const { status: eligibilityStatus } = useProviderCheckoutEligibility({
    enabled: isProvider,
    pwid: user?.pwid,
  });

  // Check for legacy ?policy=terms-of-service redirect
  const policyRedirect = usePolicyRedirect();
  if (policyRedirect) {
    return policyRedirect;
  }

  if (status === AUTH_STATUS.LOADING) {
    return <Spinner />;
  }

  // Authenticated users get redirected based on their role
  if (status === AUTH_STATUS.AUTHENTICATED) {
    if (isProvider) {
      if (eligibilityStatus !== CHECKOUT_ELIGIBILITY_STATUS.ALLOWED) {
        return <Spinner />;
      }
      return <Navigate to={ROUTES.CHECKOUT_PAGE.path} replace />;
    }

    if (userCategory === AUTH_CATEGORIES.ADMIN) {
      return <Navigate to={ROUTES.ADD_PROVIDERS_TO_ROSTER_PAGE.path} replace />;
    }

    // UNSUPPORTED, UNKNOWN, or any other unhandled category - show landing
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[Routing] Unhandled user category: "${userCategory}" - showing landing page.`,
      );
    }
  }

  // Unauthenticated or unhandled category - render landing page with tracking
  return (
    <PageTracker tracking={ROUTES.LANDING_PAGE.tracking}>
      <ElevatedLandingPage />
    </PageTracker>
  );
}
